import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserCheck, Briefcase, Award, Clock } from 'lucide-react';

const COLORS = {
  primary: '#0760ad',
  secondary: '#098a48',
  tertiary: '#118a94',
  white: '#FFFFFF',
  lightGray: '#f8fafc',
  darkText: '#1a365d'
};

const DEFAULT_TEAM = {
  bgColor: COLORS.white,
  textColor: COLORS.darkText,
  teamInfo: {
    headings: {
      title: "Our Healthcare Recruitment Experts",
      subheading: "The team connecting exceptional medical talent with top healthcare facilities"
    },
    members: [
      {
        name: "Sarah Johnson",
        position: "Senior Recruitment Partner",
        image: "",
        expertise: "Physician Placement",
        years: "12+ years experience"
      }
    ]
  }
};

const Team = ({ isAdmin = false, onEdit }) => {
  const [team, setTeam] = useState(DEFAULT_TEAM);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/about/team/`);
        if (response.ok) {
          const data = await response.json();
          setTeam(data);
        } else {
          console.error('Failed to fetch team data');
        }
      } catch (error) {
        console.error('Error fetching team data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamData();
  }, []);

  if (isLoading) {
    return (
      <section className="py-16 bg-white" style={{ backgroundColor: COLORS.lightGray }}>
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white" style={{  backgroundColor:COLORS.lightGray }}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4" style={{ color: COLORS.primary }}>
            {team.teamInfo?.headings?.title || DEFAULT_TEAM.teamInfo.headings.title}
          </h2>
          <div className="w-16 h-1 mx-auto mb-6" style={{ 
            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})` 
          }}></div>
          <p className="text-lg max-w-2xl mx-auto" style={{ color:  COLORS.darkText }}>
            {team.teamInfo?.headings?.subheading || DEFAULT_TEAM.teamInfo.headings.subheading}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {team.teamInfo?.members?.map((member, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -5 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
            >
              <div className="relative h-60 overflow-hidden">
                <img 
                  src={member.image || "https://via.placeholder.com/300x400?text=Team+Member"} 
                  alt={member.name}
                  className="w-full h-full  object-center"
                 
                />
                <div 
                  className="absolute bottom-0 left-0 right-0 h-16"
                  style={{ 
                    background: `linear-gradient(to top, ${COLORS.primary}, transparent)`,
                    opacity: 0.7
                  }}
                ></div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ 
                      backgroundColor: `${COLORS.primary}20`,
                      color: COLORS.primary
                    }}
                  >
                    <UserCheck className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium" style={{ color: COLORS.tertiary }}>
                    {member.expertise || "Expertise"}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold mb-1" style={{ color: COLORS.darkText }}>
                  {member.name || "Team Member"}
                </h3>
                <p className="text-sm mb-2" style={{ color: COLORS.tertiary }}>
                  {member.position || "Position"}
                </p>
                
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" style={{ color: COLORS.secondary }} />
                  <p className="text-xs" style={{ color: COLORS.darkText }}>
                    {member.years || "Experience"}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <motion.button
            whileHover={{ scale: 1.02 }}
            className="px-6 py-3 rounded-lg font-medium text-white"
            style={{ 
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.tertiary})`,
              boxShadow: `0 4px 14px ${COLORS.primary}20`
            }}
          >
            Meet Our Full Team
          </motion.button>
          {isAdmin && (
            <button 
              onClick={onEdit}
              className="btn btn-primary mt-4"
            >
              Edit Team Section
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default Team;